import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { useThreads } from '@liveblocks/react/suspense';
import { RoomProvider, ClientSideSuspense, LiveblocksProvider } from '@liveblocks/react/suspense';
import { GlobalRoomOperationsProvider } from './GlobalRoomOperationsManager';
import { RoomOperationsProvider } from './RoomOperationsProvider';
import { RoomRegistrar, RoomType } from './RoomRegistrar';
import { useMentionSuggestions } from 'components/editor/GridDndEditor/TiptapEditor/components/Comments/hooks/useMentionSuggestions';
import { apiDocumentServiceRepository } from '../services/repositories/implementations/ApiDocumentServiceRepository.ts';
import type { ContentType } from 'utils/getContentType';
import { getLiveblocksViewerKey, saveLiveblocksViewerKey } from './liveblocksViewerKey';

export type ThreadWithOrigin = ReturnType<typeof useThreads>['threads'][number] & {
  __origin: RoomType;
};

const ThreadsContext = createContext<{ threads: ThreadWithOrigin[]; isLoading: boolean; contentId: string; selfUserId?: string } | null>(
  null
);
export const useMergedThreads = () => {
  const v = useContext(ThreadsContext);
  if (!v) throw new Error('useMergedThreads must be used under <MergedThreadsProvider/>');
  return v;
};

const getLiveblocksPublicRoomName = (contentId: string) => {
  return `content:${contentId}:${RoomType.PUBLIC}`;
};

const getLiveblocksPrivateRoomName = (contentId: string) => {
  return `content:${contentId}:${RoomType.PRIVATE}`;
};

export function MergedThreadsProvider({
  contentId,
  contentType,
  isPrivate,
  children,
  userEmail,
  viewerId,
}: {
  contentId: string;
  contentType: ContentType;
  isPrivate: boolean;
  children: React.ReactNode;
  userEmail?: string;
  viewerId?: string;
}) {
  const PRIVATE = getLiveblocksPrivateRoomName(contentId);
  const PUBLIC = getLiveblocksPublicRoomName(contentId);

  const resolveMentionSuggestions = useMentionSuggestions({ isPrivate, contentId });
  const editorRepository = apiDocumentServiceRepository;

  const [internal, setInternal] = useState<ThreadWithOrigin[]>([]);
  const [external, setExternal] = useState<ThreadWithOrigin[]>([]);
  const [isInternalInitialized, setIsInternalInitialized] = useState(!isPrivate);
  const [isExternalInitialized, setIsExternalInitialized] = useState(false);
  const [selfUserId, setSelfUserId] = useState<string | undefined>();

  const merged = useMemo(() => {
    return [...internal, ...external];
  }, [internal, external]);

  const liveblocksKey = useMemo(() => {
    return `${contentId}:${userEmail || ''}:${viewerId || ''}`;
  }, [contentId, userEmail, viewerId]);

  const stableKeyRef = useRef(liveblocksKey);

  useEffect(() => {
    setInternal([]);
    setExternal([]);
    setIsInternalInitialized(!isPrivate);
    setIsExternalInitialized(false);
    setSelfUserId(undefined);
    stableKeyRef.current = liveblocksKey;
  }, [contentId, isPrivate, userEmail, liveblocksKey]);

  const isTransitioning = stableKeyRef.current !== liveblocksKey;

  const handleInternalThreadsChange = useCallback((threads: ThreadWithOrigin[]) => {
    setInternal(threads);
    setIsInternalInitialized(true);
  }, []);

  const handleExternalThreadsChange = useCallback((threads: ThreadWithOrigin[]) => {
    setExternal(threads);
    setIsExternalInitialized(true);
  }, []);

  // Memoized - a fresh authEndpoint reference on every render (even without
  // liveblocksKey changing) was enough to loop the Liveblocks client's re-auth.
  const authEndpoint = useCallback(async () => {
    if (isPrivate) {
      return editorRepository.authLiveblocksPrivate(contentId);
    }
    const payload = { user: { email: userEmail || '', viewerId }, viewerKey: getLiveblocksViewerKey() };
    const response = await editorRepository.authLiveblocksPublic(contentId, payload);
    if (response.viewerKey) {
      saveLiveblocksViewerKey(response.viewerKey);
    }
    setSelfUserId(response.userId);
    return response;
  }, [isPrivate, contentId, userEmail, viewerId, editorRepository]);

  const resolveUsers = useCallback(
    async ({ userIds }: { userIds: string[] }) => {
      try {
        return await editorRepository.resolveLiveblocksUsers(userIds, contentId);
      } catch {
        // Own network/transport failures only — the endpoint itself never throws (lookup
        // misses come back as a placeholder name), so this is just a defensive backstop.
        return undefined;
      }
    },
    [contentId, editorRepository]
  );

  return (
    <LiveblocksProvider
      key={liveblocksKey}
      authEndpoint={authEndpoint}
      resolveMentionSuggestions={resolveMentionSuggestions}
      resolveUsers={resolveUsers}
    >
      <GlobalRoomOperationsProvider>
        {isPrivate && (
          <RoomProvider id={PRIVATE}>
            <RoomOperationsProvider roomType={RoomType.PRIVATE}>
              <ClientSideSuspense fallback={null}>
                <RoomRegistrar
                  roomType={RoomType.PRIVATE}
                  contentId={contentId}
                  contentType={contentType}
                  onChange={handleInternalThreadsChange}
                />
              </ClientSideSuspense>
            </RoomOperationsProvider>
          </RoomProvider>
        )}

        <RoomProvider id={PUBLIC}>
          <RoomOperationsProvider roomType={RoomType.PUBLIC}>
            <ClientSideSuspense fallback={null}>
              <RoomRegistrar
                roomType={RoomType.PUBLIC}
                contentId={contentId}
                contentType={contentType}
                onChange={handleExternalThreadsChange}
              />
            </ClientSideSuspense>
          </RoomOperationsProvider>
        </RoomProvider>

        <ThreadsContext.Provider
          value={{
            threads: isTransitioning ? [] : merged,
            isLoading: isTransitioning || (isPrivate ? !isInternalInitialized || !isExternalInitialized : !isExternalInitialized),
            contentId,
            selfUserId,
          }}
        >
          {children}
        </ThreadsContext.Provider>
      </GlobalRoomOperationsProvider>
    </LiveblocksProvider>
  );
}
